import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';

export interface CommitteePersonFieldOptions {
  nationalityOptions: any[];
  departmentOptions: any[];
  designationOptions: any[];
  qualificationOptions: any[];
  countryOptions: any[];
  isCnicLifetime: boolean;
  /** UBO form only: adds the `% of Shareholding` field after Current Profession Experience. */
  includeShareholding?: boolean;
  /** UBO form only: adds the `Is Director` checkbox before the shareholding field. */
  includeIsDirector?: boolean;
}

/** Designation "CEO" (pick-list group 41): Name of Department and Head Of Department are optional for it. */
export const CEO_DESIGNATION_SEQ = 4806;

const DEPARTMENT_FIELD_KEYS = ['dptNm', 'hod'];

/**
 * Name of Department and Head Of Department are required for every designation
 * except CEO. Used by the Key Management, UBO, Directors and Committee Member popups.
 */
export function applyDesignationFieldState(
  fields: DynamicFieldConfig[],
  dsgntn: any,
): DynamicFieldConfig[] {
  const required = Number(dsgntn) !== CEO_DESIGNATION_SEQ;

  return fields.map((field) =>
    DEPARTMENT_FIELD_KEYS.includes(field.key)
      ? { ...field, validations: { ...field.validations, required } }
      : field,
  );
}

/**
 * Field layout shared by the Key Management, UBO and Directors popups, so the
 * three Committee Members forms stay identical. `% of Shareholding` is the
 * only field a form can opt into.
 */
export function buildCommitteePersonFields(
  options: CommitteePersonFieldOptions,
): DynamicFieldConfig[] {
  return [
    { key: 'prsnSeq', label: 'ID', type: 'text', hidden: true },
    {
      key: 'flNm',
      label: 'ONB_PERSON.FULL_NAME',
      type: 'text',
      validations: { minLength: 3, maxLength: 300, required: true, textOnly: true },
    },
    {
      key: 'fthrHsbndNm',
      label: 'ONB_PERSON.FATHER_HUSBAND_NAME',
      type: 'text',
      validations: { minLength: 3, maxLength: 300, required: true, textOnly: true },
    },
    {
      key: 'ntnlty',
      label: 'ONB_PERSON.NATIONALITY',
      type: 'select',
      options$: options.nationalityOptions,
      validations: { required: true },
    },
    {
      key: 'cnc',
      label: 'ONB_PERSON.CNIC',
      type: 'text',
      maxlength: 13,
      inputmode: 'numeric',
      validations: { required: true, cnic: true },
    },
    {
      key: 'cncIssuDt',
      label: 'ONB_PERSON.CNIC_ISSUE_DATE',
      type: 'date',
      validations: { required: true, noFuturDate: true },
    },
    {
      key: 'isCnicLifetime',
      label: 'ONB_PERSON.LIFETIME_CNIC',
      type: 'checkbox',
      value: false,
    },
    {
      key: 'cncExpryDt',
      label: 'ONB_PERSON.CNIC_EXPIRY_DATE',
      type: 'date',
      disabled: options.isCnicLifetime,
      validations: { required: true, futureDateOnly: true },
    },
    {
      key: 'psprt',
      label: 'ONB_PERSON.PASSPORT_NO',
      type: 'text',
      inputmode: 'Text',
      maxlength: 16,
      validations: {
        passport: true,
      },
    },
    {
      key: 'psprtIssuDt',
      label: 'ONB_PERSON.PASSPORT_ISSUE_DATE',
      type: 'date',
      validations: { noFuturDate: true },
    },
    {
      key: 'psprtExpryDt',
      label: 'ONB_PERSON.PASSPORT_EXPIRY_DATE',
      type: 'date',
      validations: { futureDateOnly: true },
    },
    {
      key: 'dob',
      label: 'ONB_PERSON.DATE_OF_BIRTH',
      type: 'date',
      validations: { required: true, minAge: 18 },
    },
    {
      key: 'dptNm',
      label: 'ONB_PERSON.DEPARTMENT_NAME',
      type: 'select',
      options$: options.departmentOptions,
      validations: { required: true },
    },
    {
      key: 'hod',
      label: 'ONB_PERSON.HEAD_OF_DEPARTMENT',
      type: 'text',
      validations: { minLength: 3, maxLength: 300, required: true, textOnly: true },
    },
    {
      key: 'dsgntn',
      label: 'ONB_PERSON.DESIGNATION',
      type: 'select',
      options$: options.designationOptions,
      validations: { required: true },
    },
    {
      key: 'qlfctn',
      label: 'ONB_PERSON.QUALIFICATION',
      type: 'select',
      options$: options.qualificationOptions,
      validations: { required: true },
    },
    {
      key: 'crntPrfssnExp',
      label: 'ONB_PERSON.CURRENT_PROFESSION_EXPERIENCE',
      type: 'text',
      validations: { minLength: 3, maxLength: 300, required: true },
    },
    ...(options.includeIsDirector
      ? [
          {
            key: 'isDrctr',
            label: 'ONB_PERSON.IS_DIRECTOR',
            type: 'checkbox',
            value: false,
          } as DynamicFieldConfig,
        ]
      : []),
    ...(options.includeShareholding
      ? [
          {
            key: 'shrhldng',
            label: 'ONB_PERSON.PERCENT_SHAREHOLDING',
            type: 'number',
            validations: {
              required: true,
              min: 0,
              max: 100,
              nonNegativeDecimal: true,
              decimalPlaces: 2,
            },
            errorMessages: {
              min: 'ONB_PERSON.SHAREHOLDING_MIN_ERROR',
              max: 'ONB_PERSON.SHAREHOLDING_MAX_ERROR',
              nonNegativeDecimal: 'ONB_PERSON.SHAREHOLDING_DECIMAL_ERROR',
            },
          } as DynamicFieldConfig,
        ]
      : []),
    {
      key: 'cntrySeq',
      label: 'ONB_PERSON.COUNTRY',
      type: 'select',
      options$: options.countryOptions,
      validations: { required: true },
    },
    {
      key: 'stSeq',
      label: 'ONB_PERSON.STATE_PROVINCE',
      type: 'select',
      options$: [],
      validations: { required: true },
    },
    {
      key: 'dstrctSeq',
      label: 'ONB_PERSON.DISTRICT',
      type: 'select',
      options$: [],
      validations: { required: true },
    },
    {
      key: 'thslSeq',
      label: 'ONB_PERSON.TEHSIL',
      type: 'select',
      options$: [],
      validations: { required: true },
    },
    {
      key: 'citySeq',
      label: 'ONB_PERSON.CITY',
      type: 'select',
      options$: [],
      validations: { required: true },
    },
    {
      key: 'rsdntlAddr',
      label: 'ONB_PERSON.RESIDENTIAL_ADDRESS',
      type: 'textarea',
      validations: { required: true },
    },
    {
      key: 'cnicPassportDoc',
      label: 'ONB_PERSON.CNIC_PASSPORT',
      type: 'file',
      accept:
        '.pdf,.doc,.docx,application/pdf,application/msword,' +
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      allowedFileTypes: [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ],
      validations: { required: true },
      errorMessages: { invalidFileType: 'ONB_PERSON.ONLY_PDF_WORD_ALLOWED' },
    },
  ];
}
